import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _noop from "lodash/noop";
import _isNumber from "lodash/isNumber";
import _isNil from "lodash/isNil";
import cx from 'classnames';
import React from 'react';
import { jsx as ___EmotionJSX } from "@emotion/react";
export const PLAYBACK_RATES = ['0.5', '0.75', '1', '1.25', '1.5', '2'];
export const QUALITIES = ['auto', '1080p', '720p', '540p', '360p'];
const buildUrl = _ref => {
  let {
    hlsLink,
    mp4Link,
    playbackRate,
    playing,
    projectorKey,
    projectorUrl,
    quality,
    subtitleLanguage
  } = _ref;
  let src = "".concat(projectorUrl, "/?auto_play=").concat(playing ? 'play' : 'pause');
  if (!_isNil(projectorKey)) {
    src = "".concat(src, "&projector_key=").concat(projectorKey);
  }
  if (!_isNil(hlsLink)) {
    src = "".concat(src, "&video_hls=").concat(hlsLink);
  } else if (!_isNil(mp4Link)) {
    src = "".concat(src, "&video_link=").concat(mp4Link);
  }
  if (!_isNil(playbackRate)) {
    src = "".concat(src, "&playback_rate=").concat(playbackRate);
  }
  if (!_isNil(quality)) {
    src = "".concat(src, "&quality=").concat(quality);
  }
  if (subtitleLanguage) {
    src = "".concat(src, "&subtitles=").concat(subtitleLanguage);
  }
  return src;
};
class VideoPlayer extends React.Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "iframe", React.createRef());
    _defineProperty(this, "handlePostMessage", message => {
      const {
        onEnd,
        onFullScreen,
        onSetPlaybackRate,
        onSetPlaying,
        onSetQuality,
        onSetSubtitle,
        onSetVolume,
        onTimeUpdate,
        onVideoDownloaded,
        playing,
        projectorUrl,
        setDuration,
        trackPlayerEvent
      } = this.props;
      if (message.origin === projectorUrl) {
        switch (message.data.type) {
          case 'END_PLAYBACK':
            onEnd();
            break;
          case 'UPDATE_TIME':
            if (onTimeUpdate != null) {
              onTimeUpdate({
                duration: message.data.duration,
                time: message.data.time
              });
            }
            if (!playing) {
              // Pause video if it should not be playing. (i.e. when the user is scrubbing while paused)
              this.pause();
            }
            break;
          case 'UPDATE_RATE':
            onSetPlaybackRate(message.data.rate.slice(0, -1));
            break;
          case 'UPDATE_QUALITY':
            onSetQuality(message.data.quality);
            break;
          case 'VIDEO_PLAYING':
            onSetPlaying(message.data.playing);
            break;
          case 'UPDATE_SUBTITLES':
            onSetSubtitle(message.data.language);
            break;
          case 'UPDATE_VOLUME':
            onSetVolume(message.data.volume);
            break;
          case 'VIDEO_DOWNLOADED':
            onVideoDownloaded();
            break;
          case 'VIDEO_FULL_SCREEN':
            onFullScreen();
            break;
          case 'VIDEO_DURATION':
            setDuration(message.data.duration);
            break;
          case 'TRACK_STRUCT_EVENT':
            trackPlayerEvent({
              action: message.data.action,
              category: 'projector',
              label: message.data.label,
              property: message.data.property,
              value: message.data.value
            });
            break;
          default:
          // Do nothing
        }
      }
    });
  }
  componentDidMount() {
    this.setIframeSrc();
    window.removeEventListener('message', this.handlePostMessage);
    window.addEventListener('message', this.handlePostMessage);
  }

  // Only reloads the video when the key or source changes, for other changes,
  // use the messages api of the projector to control.
  componentDidUpdate(prevProps) {
    const {
      hlsLink,
      mp4Link,
      playing,
      projectorKey,
      selectedProgress
    } = this.props;
    if (projectorKey !== prevProps.projectorKey || mp4Link !== prevProps.mp4Link || hlsLink !== prevProps.hlsLink) {
      this.setIframeSrc();
    } else if (_isNumber(selectedProgress)) {
      this.setProgress(selectedProgress);
      this.props.setSelectedProgress(undefined);
    } else if (playing) {
      this.play();
    } else {
      this.pause();
    }
  }
  componentWillUnmount() {
    window.removeEventListener('message', this.handlePostMessage);
  }
  setIframeSrc() {
    const {
      hlsLink,
      mp4Link,
      playbackRate,
      playing,
      projectorKey,
      projectorUrl,
      quality,
      selectedSubtitle
    } = this.props;
    const iframe = this.iframe.current;
    if (iframe == null) {
      return;
    }
    iframe.src = buildUrl({
      hlsLink,
      mp4Link,
      playbackRate,
      playing,
      projectorKey,
      projectorUrl,
      quality,
      subtitleLanguage: selectedSubtitle
    });
  }
  play() {
    var _this$iframe$current;
    const contentWindow = (_this$iframe$current = this.iframe.current) === null || _this$iframe$current === void 0 ? void 0 : _this$iframe$current.contentWindow;
    if (contentWindow == null) {
      return;
    }
    contentWindow.postMessage({
      type: 'PLAY_VIDEO'
    }, '*');
  }
  pause() {
    var _this$iframe$current2;
    const contentWindow = (_this$iframe$current2 = this.iframe.current) === null || _this$iframe$current2 === void 0 ? void 0 : _this$iframe$current2.contentWindow;
    if (contentWindow == null) {
      return;
    }
    contentWindow.postMessage({
      type: 'PAUSE_VIDEO'
    }, '*');
  }
  setProgress(progress) {
    var _this$iframe$current3;
    const contentWindow = (_this$iframe$current3 = this.iframe.current) === null || _this$iframe$current3 === void 0 ? void 0 : _this$iframe$current3.contentWindow;
    if (contentWindow == null) {
      return;
    }
    contentWindow.postMessage({
      progress,
      type: 'SEEK_VIDEO'
    }, '*');
  }
  render() {
    const {
      extraClass,
      height,
      title,
      width
    } = this.props;
    return ___EmotionJSX("iframe", {
      allowFullScreen: true,
      className: cx(extraClass, 'dc-u-b-none'),
      ref: this.iframe,
      style: {
        height,
        width
      },
      title: title
    });
  }
}
_defineProperty(VideoPlayer, "defaultProps", {
  extraClass: '',
  height: '428px',
  hlsLink: undefined,
  mp4Link: undefined,
  onEnd: _noop,
  onFullScreen: _noop,
  onSetPlaybackRate: _noop,
  onSetPlaying: _noop,
  onSetQuality: _noop,
  onSetSubtitle: _noop,
  onSetVolume: _noop,
  onTimeUpdate: _noop,
  onVideoDownloaded: _noop,
  playbackRate: undefined,
  playing: false,
  projectorKey: undefined,
  projectorUrl: '',
  quality: undefined,
  selectedProgress: undefined,
  selectedSubtitle: undefined,
  setDuration: _noop,
  setSelectedProgress: _noop,
  title: 'video exercise',
  trackPlayerEvent: _noop,
  width: '760px'
});
export default VideoPlayer;